/**
 * useMessageStore Hook
 *
 * Single source of truth for all Solomon conversation messages.
 * Unifies insights and chat messages into one stream.
 * Follows Unix philosophy: one file, one job.
 *
 * @example
 * const { messages, addUserMessage, addAssistantMessage, addInsight, clear } = useMessageStore();
 */

import { useState, useCallback, useMemo } from 'react';

// ============================================================================
// Types
// ============================================================================

/** Tool use within an assistant message */
export interface SolomonToolUse {
  tool: string;
  toolCallId: string;
  input?: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
}

/** A message in the conversation */
export interface SolomonMessage {
  /** Unique message ID */
  id: string;
  /** Message role */
  role: 'user' | 'assistant' | 'system';
  /** Message content */
  content: string;
  /** Extended thinking content (Claude's internal reasoning) */
  thinking?: string;
  /** Tool uses within this message */
  toolUses?: SolomonToolUse[];
  /** Whether this message is an initial insight */
  isInsight?: boolean;
  /** Message timestamp */
  timestamp: Date;
}

export interface UseMessageStoreOptions {
  /** Maximum messages to keep (0 = unlimited) */
  maxMessages?: number;
}

export interface UseMessageStoreReturn {
  /** All messages in chronological order */
  messages: SolomonMessage[];
  /** Whether there are any messages */
  hasMessages: boolean;
  /** Count of messages */
  messageCount: number;
  /** Set an initial insight as the first message */
  setInitialInsight: (content: string) => void;
  /** Add a user message */
  addUserMessage: (content: string) => SolomonMessage;
  /** Add an assistant message */
  addAssistantMessage: (
    content: string,
    options?: { thinking?: string; toolUses?: SolomonToolUse[] }
  ) => SolomonMessage;
  /** Add a tool result to the last assistant message */
  addToolResult: (toolCallId: string, result: unknown, isError?: boolean) => void;
  /** Update the last assistant message (for streaming) */
  updateLastAssistant: (content: string, thinking?: string) => void;
  /** Get the last message */
  getLastMessage: () => SolomonMessage | null;
  /** Clear all messages */
  clear: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function generateMessageId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================================================
// Hook
// ============================================================================

export function useMessageStore({ maxMessages = 0 }: UseMessageStoreOptions = {}): UseMessageStoreReturn {
  const [messages, setMessages] = useState<SolomonMessage[]>([]);

  // Set initial insight as first message (idempotent - won't update if content is the same)
  const setInitialInsight = useCallback((content: string) => {
    if (!content) return;

    setMessages((prev) => {
      // Check if insight already exists with the same content
      const existingInsight = prev.find((m) => m.isInsight);
      if (existingInsight && existingInsight.content === content) {
        // No change needed - return same array reference to avoid re-render
        return prev;
      }

      // Remove any existing insight
      const withoutInsight = prev.filter((m) => !m.isInsight);

      const insightMessage: SolomonMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content,
        isInsight: true,
        timestamp: new Date(),
      };

      // Insert insight at the beginning
      return [insightMessage, ...withoutInsight];
    });
  }, []);

  // Add a user message
  const addUserMessage = useCallback(
    (content: string): SolomonMessage => {
      const message: SolomonMessage = {
        id: generateMessageId(),
        role: 'user',
        content,
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const newMessages = [...prev, message];
        // Trim if maxMessages is set
        if (maxMessages > 0 && newMessages.length > maxMessages) {
          return newMessages.slice(-maxMessages);
        }
        return newMessages;
      });

      return message;
    },
    [maxMessages]
  );

  // Add an assistant message
  const addAssistantMessage = useCallback(
    (content: string, options?: { thinking?: string; toolUses?: SolomonToolUse[] }): SolomonMessage => {
      const message: SolomonMessage = {
        id: generateMessageId(),
        role: 'assistant',
        content,
        thinking: options?.thinking,
        toolUses: options?.toolUses,
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const newMessages = [...prev, message];
        // Trim if maxMessages is set
        if (maxMessages > 0 && newMessages.length > maxMessages) {
          return newMessages.slice(-maxMessages);
        }
        return newMessages;
      });

      return message;
    },
    [maxMessages]
  );

  // Add a tool result to the last assistant message
  const addToolResult = useCallback((toolCallId: string, result: unknown, isError = false) => {
    setMessages((prev) => {
      // Find the last assistant message
      const lastIndex = prev.length - 1;
      if (lastIndex < 0) return prev;

      const lastMessage = prev[lastIndex];
      if (lastMessage.role !== 'assistant' || !lastMessage.toolUses) return prev;

      // Find and update the tool use
      const updatedToolUses = lastMessage.toolUses.map((tu) =>
        tu.toolCallId === toolCallId ? { ...tu, result, isError } : tu
      );

      // Return updated messages array
      return [...prev.slice(0, lastIndex), { ...lastMessage, toolUses: updatedToolUses }];
    });
  }, []);

  // Update the last assistant message (for streaming)
  const updateLastAssistant = useCallback((content: string, thinking?: string) => {
    setMessages((prev) => {
      const lastIndex = prev.length - 1;
      if (lastIndex < 0) return prev;

      const lastMessage = prev[lastIndex];
      if (lastMessage.role !== 'assistant') return prev;

      return [
        ...prev.slice(0, lastIndex),
        {
          ...lastMessage,
          content,
          thinking: thinking ?? lastMessage.thinking,
        },
      ];
    });
  }, []);

  // Get the last message
  const getLastMessage = useCallback((): SolomonMessage | null => {
    return messages.length > 0 ? messages[messages.length - 1] : null;
  }, [messages]);

  // Clear all messages
  const clear = useCallback(() => {
    setMessages([]);
  }, []);

  // Derived state
  const hasMessages = messages.length > 0;
  const messageCount = messages.length;

  return useMemo(
    () => ({
      messages,
      hasMessages,
      messageCount,
      setInitialInsight,
      addUserMessage,
      addAssistantMessage,
      addToolResult,
      updateLastAssistant,
      getLastMessage,
      clear,
    }),
    [
      messages,
      hasMessages,
      messageCount,
      setInitialInsight,
      addUserMessage,
      addAssistantMessage,
      addToolResult,
      updateLastAssistant,
      getLastMessage,
      clear,
    ]
  );
}

export default useMessageStore;
